import { useState, useMemo } from 'react';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { CircularProgress, Box } from '@mui/material';
import { store, persistor } from './store';
import { buildTheme } from './theme';
import { ThemeModeContext } from './theme/ThemeModeContext';
import { GlobalSnackbar } from './components/GlobalSnackbar';
import { MonedasCatalogoLoader } from './components/MonedasCatalogoLoader';
import { RecordingProvider } from './features/gustavo/context/RecordingContext';

export type AppRouter = ReturnType<typeof createBrowserRouter>;

// Providers compartidos por las tres apps del split (imperium-web, crm,
// booster-web) y por el bundle combinado. Lo único que cambia entre apps es el
// `router` que se le pasa. El theme GLOBAL es Imperium (dorado); el CRM y la web
// Booster se revisten con la marca Booster vía <CrmThemeProvider> en sus rutas.
export function AppShell({ router }: { router: AppRouter }) {
  // Arranca desde la preferencia guardada (el toggle la persiste). Sin guardado → oscuro.
  const [mode, setMode] = useState<'light' | 'dark'>(() => {
    try {
      const saved = localStorage.getItem('imperium_theme');
      if (saved === 'light' || saved === 'dark') return saved;
    } catch { /* ignore */ }
    return 'dark';
  });
  const theme = useMemo(() => buildTheme(mode), [mode]);

  const toggleTheme = () => {
    setMode(prev => {
      const next = prev === 'dark' ? 'light' : 'dark';
      try { localStorage.setItem('imperium_theme', next); } catch { /* ignore */ }
      return next;
    });
  };

  return (
    <ThemeModeContext.Provider value={{ mode, toggleTheme }}>
      <Provider store={store}>
        <PersistGate
          loading={
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
              <CircularProgress />
            </Box>
          }
          persistor={persistor}
        >
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <RecordingProvider>
              <RouterProvider router={router} />
              <GlobalSnackbar />
              <MonedasCatalogoLoader />
            </RecordingProvider>
          </ThemeProvider>
        </PersistGate>
      </Provider>
    </ThemeModeContext.Provider>
  );
}
